import React, { Fragment } from 'react';
import { Spinner } from 'reactstrap';
import { isEmpty } from 'lodash-es';
import { formatMinutes } from '@s20/common/util/number';
import CommonContext from '../Common';
import {
    FlexColumnStart, FlexStartRow,
} from '../common/forms/FormElements';
import { Textarea } from '../common/forms/Textarea';
import ValidatedSelect from '../common/forms/ValidatedSelect';

const TimesheetRequireNoteTypes = {
    DriveTime: 1,
    MissedBreak: 2,
    PayrollCharge: 3,
    Overlap: 4,
};

const MissedBreakOptions = [
    { label: 0, value: 0 },
    { label: 1, value: 1 },
    { label: 2, value: 2 },
    { label: 3, value: 3 },
];

const formatTimeLabel = (timeCharge) => {
    if (timeCharge.chargeType.includes('Drop-Off')) {
        return `Dropped off @ ${timeCharge.startTime}`;
    }
    if (timeCharge.chargeType.includes('Pick-Up')) {
        return `Picked up @ ${timeCharge.startTime}`;
    }
    if (timeCharge.endTime) {
        return `${timeCharge.startTime} - ${timeCharge.endTime}`;
    }
    return timeCharge.startTime;
};

export class SignatureReviewTable extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
        };
    }

    render() {
        if (this.state.loading) {
            return (<Spinner />);
        }

        const { requiredDetailNotes, onChangeNote } = this.props;

        return (
            <table className="table table-sm table-bordered signature-review-table mb-0">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th>Charge Type</th>
                        <th>Quantity</th>
                        <th>Units</th>
                    </tr>
                </thead>
                <tbody>
                    {!(this.props.timeCharges ?? []).length
                        ? (
                            <tr>
                                <td
                                    colSpan="4"
                                    className="text-center"
                                >
                                    No employee time charges found.
                                </td>
                            </tr>
                        )
                        : this.props.timeCharges.map((timeCharge) => {
                            let requiredNote = null;

                            if (timeCharge.timesheetDetailId && !isEmpty(requiredDetailNotes)) {
                                requiredNote = requiredDetailNotes.find((x) => x.timesheetDetailId === timeCharge.timesheetDetailId);
                            }

                            const noteId = `detail-${timeCharge.timesheetDetailId}-notes`;
                            const quantityId = `detail-${timeCharge.timesheetDetailId}-quantity`;

                            return (
                                <Fragment key={timeCharge.id}>
                                    <tr>
                                        <td>
                                            {timeCharge.description}
                                        </td>

                                        <td>
                                            <FlexColumnStart>
                                                <FlexStartRow>{timeCharge.chargeType}</FlexStartRow>

                                                {!!timeCharge.startTime && !timeCharge.payrollChargeTypeId && (
                                                    <FlexStartRow>
                                                        <small className="text-info font-weight-bold">
                                                            {formatTimeLabel(timeCharge)}
                                                        </small>
                                                    </FlexStartRow>
                                                )}

                                                {timeCharge.estimatedDriveTime > 0 && (
                                                    <FlexStartRow>
                                                        <span className="text-muted text-small">
                                                            Estimated travel time: {formatMinutes(timeCharge.estimatedDriveTime)}
                                                        </span>
                                                    </FlexStartRow>
                                                )}
                                            </FlexColumnStart>
                                        </td>
                                        <td>
                                            {timeCharge.isBreak
                                                ? (
                                                    <span className="text-danger">
                                                        {timeCharge.value}
                                                    </span>
                                                )
                                                : timeCharge.value}
                                        </td>
                                        <td>{timeCharge.chargeTypeUnit}</td>
                                    </tr>

                                    {Boolean(requiredNote) && (
                                        <tr>
                                            {requiredNote.timesheetRequiredNoteType === TimesheetRequireNoteTypes.DriveTime && (
                                                <td
                                                    colSpan={5}
                                                    className="p-2"
                                                >
                                                    <Textarea
                                                        formGroupClassName="mb-0"
                                                        name={noteId}
                                                        id={noteId}
                                                        onChange={(e) => onChangeNote(timeCharge.timesheetDetailId, 'notes', e.target.value)}
                                                        label="⚠️ Drive time discrepancies detected. Please provide an explanation."
                                                        value={timeCharge.notes}
                                                        required
                                                        error="You must provide an explanation for the length of this drive time."
                                                    />
                                                </td>
                                            )}

                                            {requiredNote.timesheetRequiredNoteType === TimesheetRequireNoteTypes.MissedBreak && (
                                                <>
                                                    <td
                                                        colSpan={2}
                                                        className="p-2"
                                                    >
                                                        <Textarea
                                                            formGroupClassName="mb-0"
                                                            name={noteId}
                                                            id={noteId}
                                                            onChange={(e) => onChangeNote(timeCharge.timesheetDetailId, 'notes', e.target.value)}
                                                            label="⚠️ Please provide an explanation for your missed lunch"
                                                            value={timeCharge.notes}
                                                            required
                                                            error="You must provide an explanation for why you missed your lunch"
                                                        />
                                                    </td>

                                                    <td
                                                        colSpan={2}
                                                        className="p-2"
                                                    >
                                                        <ValidatedSelect
                                                            id={quantityId}
                                                            name={quantityId}
                                                            label="⚠️ How many lunches did you miss?"
                                                            formGroupClassName="mb-0"
                                                            required
                                                            options={MissedBreakOptions}
                                                            hideClear
                                                            value={
                                                                MissedBreakOptions.find((s) => s.value === timeCharge.quantity)
                                                                ?? ''
                                                            }
                                                            onChange={(selection) => onChangeNote(timeCharge.timesheetDetailId, 'quantity', selection?.value)}
                                                            error="You must select how many lunches were missed"
                                                        />
                                                    </td>
                                                </>
                                            )}

                                            {requiredNote.timesheetRequiredNoteType === TimesheetRequireNoteTypes.PayrollCharge && (
                                                <td
                                                    colSpan={5}
                                                    className="p-2"
                                                >
                                                    <Textarea
                                                        formGroupClassName="mb-0"
                                                        name={noteId}
                                                        id={noteId}
                                                        onChange={(e) => onChangeNote(timeCharge.timesheetDetailId, 'notes', e.target.value)}
                                                        label="⚠️ Please add a note for why you added this charge"
                                                        value={timeCharge.notes}
                                                        required
                                                        error="You must provide a note for this charge"
                                                    />
                                                </td>
                                            )}

                                            {requiredNote.timesheetRequiredNoteType === TimesheetRequireNoteTypes.Overlap && (
                                                <td
                                                    colSpan={5}
                                                    className="p-2"
                                                >
                                                    <Textarea
                                                        formGroupClassName="mb-0"
                                                        name={noteId}
                                                        id={noteId}
                                                        onChange={(e) => onChangeNote(timeCharge.timesheetDetailId, 'notes', e.target.value)}
                                                        label={`⚠️ This time overlaps with time on timesheet ${requiredNote.linkedTimesheetNumber}`}
                                                        value={timeCharge.notes}
                                                        required
                                                        error="You must provide a note for this possible overlap"
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                </tbody>
            </table>
        );
    }
}

SignatureReviewTable.contextType = CommonContext;
